import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';

interface AOIDrawerProps {
  onAOIComplete?: (geometry: any) => void;
  isDrawing: boolean;
}

export function AOIDrawer({ onAOIComplete, isDrawing }: AOIDrawerProps) {
  const map = useMap();
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const drawControlRef = useRef<L.Control.Draw | null>(null);

  useEffect(() => {
    if (!map) return;

    // Add feature group to map
    const drawnItems = drawnItemsRef.current;
    map.addLayer(drawnItems);

    // Create draw control
    const drawControl = new L.Control.Draw({
      draw: {
        polygon: {
          allowIntersection: false,
          shapeOptions: {
            color: '#3B82F6',
            weight: 2,
            fillOpacity: 0.2
          }
        },
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false
      },
      edit: {
        featureGroup: drawnItems,
        remove: false
      }
    });

    drawControlRef.current = drawControl;

    // Normalize longitude to -180 to +180 range
    const normalizeLongitude = (lng: number): number => {
      while (lng > 180) lng -= 360;
      while (lng < -180) lng += 360;
      return lng;
    };

    // Handle draw created event
    map.on(L.Draw.Event.CREATED, (event: any) => {
      const layer = event.layer;
      drawnItems.clearLayers();
      drawnItems.addLayer(layer);

      // Convert to our geometry format with normalized coordinates
      const latlngs = layer.getLatLngs()[0];
      const rings = [latlngs.map((ll: L.LatLng) => {
        const normalizedLng = normalizeLongitude(ll.lng);
        console.log(`[AOIDrawer] Normalizing coordinate: [${ll.lng}, ${ll.lat}] -> [${normalizedLng}, ${ll.lat}]`);
        return [normalizedLng, ll.lat];
      })];

      // Create geometry object compatible with our tile calculation
      const geometry = {
        type: 'polygon',
        rings: rings
      };

      console.log('[AOIDrawer] AOI geometry created:', geometry);
      console.log('[AOIDrawer] First coordinate:', geometry.rings[0][0]);

      if (onAOIComplete) {
        onAOIComplete(geometry);
      }
    });

    return () => {
      map.off(L.Draw.Event.CREATED);
      map.removeLayer(drawnItems);
      if (drawControlRef.current) {
        map.removeControl(drawControlRef.current);
      }
    };
  }, [map, onAOIComplete]);

  useEffect(() => {
    if (!map || !drawControlRef.current) return;

    if (isDrawing) {
      // Clear existing drawings
      drawnItemsRef.current.clearLayers();

      // Start polygon drawing
      // @ts-ignore
      new L.Draw.Polygon(map, drawControlRef.current.options.draw.polygon).enable();
    }
  }, [isDrawing, map]);

  return null;
}
